import { domainsActions, onboardingActions, sitesActions } from '@/redux/modules';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProductAddons } from '@/redux/actions/productDetail';
import { sortAlphabetic } from '@/utils/Application/sort';
import { useLocation } from 'react-router';
import { multiDomainsPlansIds } from '@/pages/Onboarding/OnboardingHandler/OnboardingHandler.enum';
import { useBlockedWindowModal } from '@/hooks/useBlockedWindowModal';
import { POPUP_BLOCKED_ORIGIN } from '@/hooks/useBlockedWindowModal/useBlockedWindowModal.enum';

import {
  FLOWS, STEPS,
} from '@/pages/sites/SitesWizardHandler/SitesWizardHandler.enum';

const useOnboardingActions = ({
  allowTempDomainCreation,
  currentHostingInitialState,
  initialStep,
  domain,
  isAgentAvailableParam,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [showAutoCompleteDomain, setShowAutoCompleteDomain] = useState(false);
  const [justUpdatedDomains, setJustUpdatedDomains] = useState(true);
  const [page, setPage] = useState(1);
  const [usingTempDomain, setUsingTempDomain] = useState(undefined);
  const [currentFlow, setCurrentFlow] = useState('');
  const [currentHosting, setCurrentHosting] = useState(currentHostingInitialState);
  const [isInternalDomain, setIsInternalDomain] = useState(false);
  const [otherToolsRedirect, setOtherToolsRedirect] = useState(null);
  const [userDomainsList, setUserDomainsList] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [selectedDomain, setSelectedDomain] = useState(domain);
  const [displayExitConfirmModal, setDisplayExitConfirmModal] = useState(false);

  const domainsReducer = useSelector(state => state.newDomains);
  const sitesReducer = useSelector(state => state.sites);
  const { setPopupBlockedModalOnCookie } = useBlockedWindowModal();

  const insideWizard = location && location.pathname && !location.pathname.includes('onboarding');

  const requestChangeDomain = useCallback(({
    newDomain,
    hostingId = null,
    isAddonDomain = false,
    awaitForAppointment = false,
    domainId = null,
    oldDomain = null,
    fallback,
  }) => {
    dispatch(domainsActions.changeDomain.request({
      domain: newDomain,
      hostingId,
      isOnboarding: insideWizard,
      isAddonDomain,
      awaitForAppointment,
      domainId,
      oldDomain,
      fallback,
    }));
  }, [dispatch, insideWizard]);

  const requestDomainsPage = useCallback(() => {
    if (justUpdatedDomains) {
      dispatch(domainsActions.domains.request({ page }));
      setJustUpdatedDomains(false);
    }
  }, [page, dispatch, justUpdatedDomains]);

  const shouldRequestNewDomainsPage = useCallback(() => {
    const hasDomains = domainsReducer.list.length > 0;
    const hasMoreDomainsPages = domainsReducer.list.length < domainsReducer.listTotal;
    const isLoadingDomains = domainsReducer.loading;

    if (hasDomains && hasMoreDomainsPages && !isLoadingDomains && !justUpdatedDomains) {
      setPage(page + 1);
      setJustUpdatedDomains(true);
    }
  }, [
    domainsReducer.loading,
    domainsReducer.list,
    domainsReducer.listTotal,
    page,
    justUpdatedDomains,
  ]);

  const cleanUp = useCallback(() => {
    dispatch(sitesActions.verifyIfSiteExist.reset());
    dispatch(sitesActions.wordpressInstallation.reset());
    dispatch(sitesActions.aditionalDomain.set({ domainLinkedToPlan: false }));
    dispatch(sitesActions.sitebuilder.createSite.reset());
    dispatch(onboardingActions.sitebuilder.provisioning.reset());
  }, [dispatch]);

  const getDomainsNextPage = useCallback(() => {
    if (page > 1) {
      dispatch(domainsActions.domains.request({ page }));
    }
  }, [
    dispatch,
    page,
  ]);

  const concatenatedDomains = useCallback(() => {
    const lists = sitesReducer.list;
    const domainsList = domainsReducer.list;
    const unifiedArray = [...lists];

    if (!domainsList && !lists) return [];

    domainsList.forEach((external) => {
      const domainEntry = {
        hosting_id: external.external_id ? parseInt(external.external_id, 10) : null,
        domain: external.domain || '',
        domain_id: external.domain_id || null,
        package_id: external.package_id || null,
        status: external.status || '',
      };

      if (!sitesReducer.list.some(hosting => hosting.domain === external.domain)) {
        unifiedArray.push(domainEntry);
      }
    });

    sitesReducer.list.forEach((hosting) => {
      const cpanelDomains = (hosting && hosting.cpanel_domains && hosting.cpanel_domains.map(cpanelDomains => cpanelDomains.domain)) || [];

      cpanelDomains.forEach((domain) => {
        if (!unifiedArray.some(hosting => hosting.domain === domain)) {
          unifiedArray.push({
            hosting_id: hosting.hosting_id,
            domain,
            domain_id: null,
            package_id: hosting.package_id,
            status: 'active',
          });
        }
      });
    });

    return unifiedArray;
  }, [sitesReducer.list, domainsReducer.list]);

  const resetAddDomainToPlan = useCallback(({ domainLinkedToPlan = false }) => {
    dispatch(sitesActions.aditionalDomain.set({ domainLinkedToPlan }));
  }, [dispatch]);

  const requestData = useCallback(() => {
    if (!domainsReducer.loaded) {
      dispatch(domainsActions.domains.request({ page }));
      dispatch(domainsActions.temporaryDomains.request());
    }

    if (!sitesReducer.loaded) {
      dispatch(sitesActions.sites.request());
    }
  }, [
    dispatch,
    sitesReducer.loaded,
    domainsReducer.loaded,
    page,
  ]);

  const getAddons = useCallback(({ currentHosting }) => {
    if (currentHosting && currentHosting.hosting_id) {
      dispatch(getProductAddons(currentHosting.hosting_id));
    }
  }, [
    dispatch,
  ]);

  const shouldUseCreateTempDomainAsDefault = useCallback(() => {
    if (insideWizard) {
      const allowTempDomainInOnboarding = !!(
        allowTempDomainCreation
        && currentFlow !== FLOWS.SITE_BUILDER
        && currentFlow !== FLOWS.WORDPRESS_MIGRATION
        && !!sitesReducer.list.filter(item => multiDomainsPlansIds.includes(item.package_id) && item.product_name && item.status === 'Active').length
      );

      setUsingTempDomain(allowTempDomainInOnboarding);
    }


    if (!insideWizard) {
      const allowTempDomainInOnboarding = !!(
        allowTempDomainCreation
        && currentFlow !== FLOWS.SITE_BUILDER
        && currentFlow !== FLOWS.WORDPRESS_MIGRATION
      );

      setUsingTempDomain(allowTempDomainInOnboarding);
    }
  }, [
    allowTempDomainCreation,
    currentFlow,
    sitesReducer.list,
    insideWizard,
  ]);

  const isWordPressAI = currentFlow === FLOWS.WORDPRESS_IA;
  const isWordPress = currentFlow === FLOWS.WORDPRESS;


  const getAssembledDomainList = useCallback(() => {
    const unifiedArray = insideWizard ? concatenatedDomains() : sitesReducer.list;
    const userDomainListSortedAlphabetically = sortAlphabetic(unifiedArray, 'domain');

    return (
      userDomainListSortedAlphabetically.filter(item => item && (item.domain !== '') && ((item.status && item.status.toLowerCase()) === 'active')).map((item, index) => {
        const itemByDomainsState = domainsReducer.list.find(domain => domain.domain === item.domain);
        const domainId = (itemByDomainsState && itemByDomainsState.id) || undefined;

        return {
          ...item,
          id: index,
          externalId: item.hosting_id,
          domain_id: domainId,
          label: item.domain,
          value: index,
        };
      })
    );
  }, [
    domainsReducer.list,
    sitesReducer,
    concatenatedDomains,
    insideWizard,
  ]);

  const addDomainToPlan = useCallback(({ domain, hostingId, selectedObject }) => {
    if (!sitesReducer.loading) {
      dispatch(sitesActions.aditionalDomain.request(domain, hostingId));
      setCurrentHosting({
        ...selectedObject,
        domain,
        external_id: hostingId,
        hosting_id: hostingId,
      });
    }
  }, [dispatch, sitesReducer.loading]);

  const checkDomainIsOnPlan = useCallback(({ userPlan, selectedDomain }) => {
    if (userPlan.domain === selectedDomain) {
      return true;
    }
    const isCpanelDomain = userPlan.cpanel_domains && userPlan.cpanel_domains.some(cpanelDomain => cpanelDomain.domain === selectedDomain);

    return isCpanelDomain;
  }, []);

  const isInternalDomainCheck = useCallback(({ domain }) => {
    const isOnDomainsList = domainsReducer.list && domainsReducer.list.some(item => item.domain === domain);
    if (isOnDomainsList) {
      return true;
    }

    const domainIsOnPlan = sitesReducer.list && sitesReducer.list.some(item => checkDomainIsOnPlan({ userPlan: item, selectedDomain: domain }));
    if (domainIsOnPlan) {
      return true;
    }

    return false;
  }, [sitesReducer.list, domainsReducer.list, checkDomainIsOnPlan]);

  const configureDns = useCallback(({
    hostingId, domain, domainId,
  }) => {
    const isTempDomain = domain.includes('.myhostgator') || domain.includes('.meusitehostgator') || domain.includes('.misitiohostgator');
    const isInternalDomain = isInternalDomainCheck({ domain });
    setIsInternalDomain(isTempDomain || isInternalDomain);

    dispatch(sitesActions.changeDomainDns.set({ domainDnsChanged: false }));
    if (isInternalDomain && domainId) {
      dispatch(sitesActions.changeDomainDns.request(
        domainId,
        hostingId,
        domain,
      ));
    }
  }, [dispatch, isInternalDomainCheck]);

  const setDefaultDomains = useCallback(({ setSelectedDomain, paramsDomain }) => {
    const domainsListLength = sitesReducer.list.length;

    if (domainsListLength) {
      const assembledDomainList = getAssembledDomainList();
      const userDomainOfParams = assembledDomainList.find(site => site.label === paramsDomain);

      const hasDiffItems = userDomainsList.length !== assembledDomainList.length
        || userDomainsList.some((item, index) => item.label !== assembledDomainList[index].label);

      if (hasDiffItems) {
        setUserDomainsList(assembledDomainList);
      }

      if (paramsDomain && userDomainOfParams) {
        setSelectedDomain(userDomainOfParams);
      }
    }
  }, [
    getAssembledDomainList,
    sitesReducer.list,
    userDomainsList,
  ]);

  const wordpressInstallationIsFinished = useCallback(({ domain }) => {
    const isWpInstallStep = currentStep === STEPS.WORDPRESS_INSTALL;
    const wordpressIsInstalled = sitesReducer.wordpressLoaded && sitesReducer.wordPressInstalled;
    const isInternal = isInternalDomainCheck({ domain });

    if (isWpInstallStep && wordpressIsInstalled) {
      const successStep = (isInternal || usingTempDomain) ? STEPS.SUCCESS : STEPS.EXTERNAL_DOMAIN;
      setCurrentStep(successStep);
    }
  }, [
    sitesReducer.wordpressLoaded,
    sitesReducer.wordPressInstalled,
    currentStep,
    usingTempDomain,
    isInternalDomainCheck,
  ]);

  const autoCompleteDomain = useCallback(({ domain }) => {
    if (!domainsReducer.loading) {
      let foundDomain = false;
      const domainsAlike = domainsReducer.list.filter((item) => {
        if (!foundDomain) {
          if (item.domain === domain) {
            foundDomain = true;
            setShowAutoCompleteDomain(false);
            return item.domain;
          }
          return item.domain.includes(domain);
        }
        return false;
      });

      userDomainsList.forEach((item) => {
        if (!domainsAlike.some(hosting => hosting.domain === item.domain)) {
          if (!foundDomain) {
            if (item.domain === domain) {
              foundDomain = true;
              setShowAutoCompleteDomain(false);
              domainsAlike.push(item);
            }

            if (item.domain.includes(domain)) {
              domainsAlike.push(item);
            }
          }
        }
      });

      setShowAutoCompleteDomain(!!domainsAlike.length);
      setAutoCompleteOptions(domainsAlike);
    }
  }, [
    domainsReducer.list,
    domainsReducer.loading,
    userDomainsList,
    setShowAutoCompleteDomain,
  ]);

  const handleVerifyIfSiteExist = useCallback(() => {
    if (!currentHosting || !currentHosting.hosting_id) return;

    const wpAiFlowCondition = (
      (sitesReducer.domainLinkedToPlan && !usingTempDomain)
        || (!sitesReducer.domainLinkedToPlan && usingTempDomain)
    );

    const defaultFlowCondition = sitesReducer.domainLinkedToPlan && !usingTempDomain;

    const shouldVerify = (isWordPressAI || isWordPress) ? wpAiFlowCondition : defaultFlowCondition;

    if (shouldVerify) {
      dispatch(sitesActions.verifyIfSiteExist.request({
        domain: currentHosting.domain || domainsReducer.createdTemporaryDomain,
        hostingId: currentHosting.hosting_id,
      }));
      resetAddDomainToPlan({ domainLinkedToPlan: true });
    }
  }, [sitesReducer.domainLinkedToPlan, currentHosting, dispatch, resetAddDomainToPlan, usingTempDomain, isWordPressAI, isWordPress, domainsReducer]);

  const checkDomainAvailabilty = useCallback(({ domain }) => {
    dispatch(sitesActions.domainAvailability.request({ domain }));
  }, [
    dispatch,
  ]);

  const onCloseWizard = useCallback(({ handleGoBack }) => {
    const hgBuilderSteps = [STEPS.IA_BUILDER_CREATE_SITE, STEPS.IA_BUILDER_WITH_TEMPLATE, STEPS.IA_BUILDER_MY_BUSINESS];

    if (hgBuilderSteps.includes(currentStep)) {
      setDisplayExitConfirmModal(true);
      return;
    }

    handleGoBack();
  }, [currentStep]);

  const provisioningSitebuilderOnHosting = useCallback(({ hostingId, callback }) => {
    if (hostingId) {
      dispatch(onboardingActions.sitebuilder.provisioning.request({ hostingId, callback }));
    }
  }, [dispatch]);

  const requestCreateSiteWithAiBuilder = useCallback(({
    builderAddonId, domain, type = 'ai-project', projectData = null, templateId = null, isAgentFactory, aiBuilderAgentData = null,
  }) => {
    const popupBlockedAction = () => {
      setPopupBlockedModalOnCookie({ origin: POPUP_BLOCKED_ORIGIN.SITE_CREATED_WITH_HG_BUILDER });
    };

    dispatch(sitesActions.sitebuilder.createSite.request({
      builderAddonId,
      domain,
      type,
      projectData,
      templateId,
      popupBlockedAction,
      isAgentFactory,
      aiBuilderAgentData,
    }));
  }, [dispatch, setPopupBlockedModalOnCookie]);

  const requestCreatePreviewWithAiBuilder = useCallback(({
    type = 'ai-project', projectData = null,
  }) => {
    dispatch(sitesActions.sitebuilder.createPreview.request({
      type,
      projectData,
    }));
  }, [dispatch]);

  const requestSyncPreviewWithAiBuilder = useCallback(({
    builderAddonId, domain, isAgentFactory, aiBuilderAgentData,
  }) => {
    dispatch(sitesActions.sitebuilder.syncPreview.request({
      builderAddonId,
      domain,
      isAgentFactory,
      aiBuilderAgentData,
    }));
  }, [dispatch]);

  const getBuilderTemplateCategories = useCallback(() => {
    dispatch(onboardingActions.sitebuilder.templates.categories.request());
  }, [dispatch]);

  const getBuilderTemplates = useCallback(({ category, page }) => {
    dispatch(onboardingActions.sitebuilder.templates.request({
      category,
      page,
      count: 20,
    }));
  }, [dispatch]);

  const getBuilderAddonIdByHostingId = useCallback((hostingId) => {
    if (!Array.isArray(sitesReducer.list)) return undefined;
    const foundSite = sitesReducer.list.find(item => item.hosting_id === hostingId);
    return foundSite ? foundSite.builder_addon_id : undefined;
  }, [sitesReducer.list]);

  const redirectPreviewPage = useCallback(() => {
    setCurrentStep(STEPS.SITE_BUILDER_PREVIEW);
  }, [setCurrentStep]);

  const redirectSuccessPageAiBuilder = useCallback(() => {
    if (isAgentAvailableParam) {
      setCurrentFlow(FLOWS.AGENT_FACTORY_ONBOARDING);
      setCurrentStep(STEPS.SUCCESS);
      return;
    }
    setCurrentFlow(FLOWS.AGENT_FACTORY);
    setCurrentStep(STEPS.SUCCESS);
  }, [setCurrentFlow, setCurrentStep, isAgentAvailableParam]);

  const dispatchClmEvent = ({ action, finished }) => {
    dispatch(onboardingActions.clmEvent.registerClmEvent({ action, finished }));
  };

  return {
    addDomainToPlan,
    autoCompleteDomain,
    autoCompleteOptions,
    checkDomainAvailabilty,
    cleanUp,
    concatenatedDomains,
    configureDns,
    currentFlow,
    currentHosting,
    currentStep,
    getAddons,
    getAssembledDomainList,
    getDomainsNextPage,
    handleVerifyIfSiteExist,
    isInternalDomain,
    isWordPressAI,
    otherToolsRedirect,
    page,
    requestChangeDomain,
    requestData,
    requestDomainsPage,
    resetAddDomainToPlan,
    selectedDomain,
    setAutoCompleteOptions,
    setCurrentFlow,
    setCurrentHosting,
    setCurrentStep,
    setDefaultDomains,
    setIsInternalDomain,
    setOtherToolsRedirect,
    setPage,
    setSelectedDomain,
    setShowAutoCompleteDomain,
    setUserDomainsList,
    setUsingTempDomain,
    shouldRequestNewDomainsPage,
    shouldUseCreateTempDomainAsDefault,
    showAutoCompleteDomain,
    userDomainsList,
    usingTempDomain,
    wordpressInstallationIsFinished,
    onCloseWizard,
    provisioningSitebuilderOnHosting,
    requestCreateSiteWithAiBuilder,
    requestCreatePreviewWithAiBuilder,
    requestSyncPreviewWithAiBuilder,
    getBuilderTemplateCategories,
    getBuilderTemplates,
    getBuilderAddonIdByHostingId,
    redirectSuccessPageAiBuilder,
    redirectPreviewPage,
    displayExitConfirmModal,
    setDisplayExitConfirmModal,
    dispatchClmEvent,
  };
};

export default useOnboardingActions;
